import { useCallback, useMemo } from "react";
import { useReports } from "../../../../hooks/useReports";
import { SalonReport } from "../../../../types/reports";
import {
  ReportMetricDef,
  ReportV2Definition,
  RunReportV2Response,
  ViewConfig,
} from "../../../../types/reportsV2";
import { notifySuccess } from "../../../../utils/notify";
import { useReportDraftSource } from "../../shell/reportsShellContext";
import { exportRunCsv } from "../export/exportReportCsv";
import { buildMetricCatalog, metricsByKey } from "../model/metricCatalog";
import { describeRun } from "../model/runContext";
import { runRequestOf } from "../model/viewConfig";
import { ReportPrintViewProps } from "../print/ReportPrintView";
import { ReportPrint, useReportPrint } from "../print/useReportPrint";
import { ReportDraft, useReportDraft } from "./useReportDraft";
import { ReportRunState, useReportRun } from "./useReportRun";
import { ReportTableModel, useReportTable } from "./useReportTable";

export interface UseReportCanvasOptions {
  salonId: string;
  report: SalonReport;
  definition: ReportV2Definition;
  /** Display name (unsaved-changes dialog, CSV and print titles). */
  name: string;
  /** Display description (print subtitle). */
  description: string;
  /** Put the saved report back into the shell's list. */
  onReportSaved: (report: SalonReport) => void;
  /** Sidebar / list unsaved dot. */
  onDirtyChange: (dirty: boolean) => void;
}

export interface ReportCanvasModel {
  draft: ReportDraft;
  /** The view on screen (the draft). */
  view: ViewConfig;
  run: ReportRunState;
  /** The latest successful result (null after a failed run). */
  data: RunReportV2Response | null;
  /** A settled result that matches the view on screen (export / print). */
  settled: RunReportV2Response | null;
  /** Static metrics plus the run's dynamic payment-method metrics. */
  catalog: ReportMetricDef[];
  byKey: ReadonlyMap<string, ReportMetricDef>;
  table: ReportTableModel;
  printer: ReportPrint;
  /** Props for the print-only copy while printing a settled result. */
  printProps: ReportPrintViewProps | null;
  exportCsv: () => void;
}

/**
 * Everything a v2 report canvas does, independent of its layout: the draft
 * vs saved view (with the navigation guard and the shell's draft source for
 * Duplicate), the debounced run, the metric catalog, the table model, CSV
 * export and print. The desktop canvas and the phone layout are two
 * presentations of this one model.
 */
export function useReportCanvas({
  salonId,
  report,
  definition,
  name,
  description,
  onReportSaved,
  onDirtyChange,
}: UseReportCanvasOptions): ReportCanvasModel {
  const { updateSalonReport } = useReports();

  const persistView = useCallback(
    async (view: ViewConfig) => {
      const { report: updated } = await updateSalonReport(salonId, report.id, {
        view_config: view,
      });
      onReportSaved(updated);
      return updated;
    },
    [updateSalonReport, salonId, report.id, onReportSaved],
  );
  const draft = useReportDraft({
    report,
    definition,
    name,
    persistView,
    onDirtyChange,
  });
  const view = draft.draft;
  // Duplicate copies what's on screen: an unsaved draft moves to the copy
  // and this report reverts to its saved view.
  useReportDraftSource(report.id, {
    getDraftViewConfig: () => (draft.dirty ? view : null),
    discardDraft: draft.discard,
  });
  const run = useReportRun({
    salonId,
    reportId: report.id,
    request: runRequestOf(view),
  });
  const data = run.error ? null : run.data;
  const settled = data && !run.loading ? data : null;
  const printer = useReportPrint(settled !== null);

  const catalog = useMemo(
    () =>
      buildMetricCatalog(definition, run.data?.metrics, [
        ...view.columns,
        ...view.kpis,
        ...(view.sort ? [view.sort.key] : []),
      ]),
    [definition, run.data, view.columns, view.kpis, view.sort],
  );
  const byKey = useMemo(() => metricsByKey(catalog), [catalog]);
  const table = useReportTable(definition, run.data, view, byKey);

  const exportCsv = () => {
    if (!settled) return;
    exportRunCsv(name, settled, table, describeRun(definition, settled, view));
    notifySuccess("Your download has started.", { title: "Report exported" });
  };

  const printProps =
    printer.printing && settled
      ? {
          name,
          description,
          data: settled,
          table,
          context: describeRun(definition, settled, view),
        }
      : null;

  return {
    draft,
    view,
    run,
    data,
    settled,
    catalog,
    byKey,
    table,
    printer,
    printProps,
    exportCsv,
  };
}
